/**
 * @module Hooks.TaxCalculation
 *
 * Custom hook for calculating sales tax via Stripe Tax API
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchPost } from "app/utils/request";

export interface TaxCalculationParams {
  amount: number; // amount in cents
  priceId: string;
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
}

export interface TaxCalculationResult {
  subtotal: number;
  taxAmount: number;
  total: number;
}

interface UseTaxCalculationReturn {
  taxData: TaxCalculationResult | null;
  isLoading: boolean;
  error: string | null;
  calculateTax: (params: TaxCalculationParams) => void;
}

/**
 * Hook to calculate sales tax for checkout
 */
export function useTaxCalculation(): UseTaxCalculationReturn {
  const [taxData, setTaxData] = useState<TaxCalculationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const calculateTax = useCallback((params: TaxCalculationParams) => {
    // Validate required fields
    if (!params.country || !params.postalCode) {
      // Don't make the request if required fields are missing
      setTaxData(null);
      setError(null);
      return;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    const requestData = {
      amount: params.amount,
      price_id: params.priceId,
      country: params.country,
      state: params.state || "",
      postal_code: params.postalCode,
      city: params.city || "",
    };

    fetchPost<TaxCalculationResult>("stripe/calculate_tax", requestData, { version: 1 })
      .then((data) => {
        setTaxData(data);
        setError(null);
      })
      .catch((err) => {
        // Only set error if it wasn't an abort
        if (err.name !== "AbortError") {
          // Set error to trigger the warning message display
          setError("invalid_address");
          setTaxData(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return {
    taxData,
    isLoading,
    error,
    calculateTax,
  };
}
